import classNames from "classnames";
import React, { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { isAgentBoardEnabled } from "@/configs/agentFeatures";
import {
  INavigation,
  NavigationGroupOrder,
  NavigationGroupTitleKeys,
  Navigations,
  resolveDefaultLandingPath,
} from "@/configs/menu";
import {
  useAccounts,
  useAuthority,
  useConfigParameters,
  useUserState,
} from "@/context/UserContext";
import useThemeMode from "@/hooks/useThemeMode";
import ExpandIcon from "@/icons/navigation/expand.svg?react";
import logoSmall from "@/images/logo-sx.png";
import logoWhite from "@/images/logo-white.png";
import logo from "@/images/logo.png";
import {
  compileNavigationRoutes,
  computeNavigationItem,
  findOwningNavigation,
} from "@/layout/v3/Navigation";
import { useSidebarContext } from "./SidebarContext";
import SidebarItem from "./SidebarItem";

const Sidebar: React.FC = () => {
  const { expanded, toggleSidebar } = useSidebarContext();
  const { t } = useTranslation();
  const groupIdPrefix = useId();
  const location = useLocation();
  const navigate = useNavigate();
  const userState = useUserState();
  const { hasAuthority } = useAuthority();
  const configParameters = useConfigParameters();

  const accounts = useAccounts(true);

  const computedNav = useMemo(
    () =>
      [...Navigations].map(item =>
        computeNavigationItem(
          item,
          accounts,
          hasAuthority,
          userState,
          configParameters,
        ),
      ),
    [accounts, hasAuthority, userState, configParameters],
  );

  const groupedNav = useMemo(() => {
    const groups: Record<string, INavigation[]> = {};
    for (const item of computedNav) {
      if (item.display === false) continue;
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group].push(item);
    }
    return groups;
  }, [computedNav]);

  // Index dividers by *rendered* position, so a leading group hidden by
  // permissions doesn't leave a stray divider at the top of the nav.
  const visibleGroups = useMemo(
    () => NavigationGroupOrder.filter(key => groupedNav[key]?.length),
    [groupedNav],
  );

  // Exactly one item is active, resolved by the shared rule the route loader
  // uses. `containersOwnDescendants` is on here: standing on any `/app/admin/**`
  // page should highlight Administration even when no entry claims that path.
  const compiledNav = useMemo(
    () => compileNavigationRoutes(computedNav.filter(i => i.display !== false)),
    [computedNav],
  );
  const activeItemKey = useMemo(
    () =>
      findOwningNavigation(location.pathname, compiledNav, {
        containersOwnDescendants: true,
      })?.key,
    [compiledNav, location.pathname],
  );

  // Same definition of "AI landing" as the route loaders: AI mode plus copilot access.
  const isAIModeEnabled =
    configParameters.isTrustDSPM === true &&
    hasAuthority("TRUSTAI_COPILOT:READ");
  const hasAccounts = Boolean(userState.user?.accounts?.length);
  const defaultLandingPath = resolveDefaultLandingPath({
    hasAccounts,
    isAgentBoardEnabled: isAgentBoardEnabled(configParameters),
    isAILanding: isAIModeEnabled,
  });

  const largeLogo = useThemeMode() === "dark" ? logoWhite : logo;

  return (
    <div className="tlx-v4-sidebar-wrapper">
      <nav
        className={classNames("tlx-v4-sidebar", {
          "tlx-v4-sidebar--collapsed": !expanded,
        })}
      >
        <div className="tlx-v4-sidebar-header">
          <img
            src={expanded ? largeLogo : logoSmall}
            width={expanded ? 178 : 41}
            height={expanded ? 30 : 29}
            style={{ cursor: "pointer" }}
            alt="TrustLogix Logo"
            onClick={() => navigate(defaultLandingPath)}
          />
        </div>

        <div className="tlx-v4-sidebar-nav">
          {visibleGroups.map((groupKey, index) => {
            const items = groupedNav[groupKey];
            const titleKey = NavigationGroupTitleKeys[groupKey];
            const title = titleKey ? t(titleKey) : null;
            const showHeader = expanded && Boolean(title);
            const headerId = `${groupIdPrefix}-${groupKey}`;
            return (
              <div
                key={groupKey}
                className="tlx-v4-sidebar-group"
                data-test-id={`sidebar-group-${groupKey}`}
                // A headerless group's nameless group role would only add
                // screen-reader noise.
                role={title ? "group" : undefined}
                aria-labelledby={showHeader ? headerId : undefined}
                // Collapsed hides the visible heading; keep the group named.
                aria-label={title && !showHeader ? title : undefined}
              >
                {showHeader ? (
                  <div id={headerId} className="tlx-v4-sidebar-group-header">
                    {title}
                  </div>
                ) : (
                  index > 0 && (
                    <div
                      className="tlx-v4-sidebar-group-divider"
                      aria-hidden="true"
                    />
                  )
                )}
                {items.map(item => (
                  <SidebarItem
                    key={item.key}
                    item={item}
                    expanded={expanded}
                    isActive={item.key === activeItemKey}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </nav>
      <button
        type="button"
        className={classNames("tlx-v4-sidebar-toggle", {
          "tlx-v4-sidebar-toggle--expanded": expanded,
        })}
        onClick={toggleSidebar}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        <ExpandIcon />
      </button>
    </div>
  );
};

export default Sidebar;
